import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import React from 'react'

import BuildsFetcher from '../../../../containers/BuildsFetcher'
import Icon from '../../Icon/Icon'
import LinkWithIcon from '../../LinkWithIcon/LinkWithIcon'
import RouterLink from '../../RouterLink/RouterLink'
import RouterLinkWrapper from '../../RouterLinkWrapper/RouterLinkWrapper'

import styles from './SnapshotDependenciesCounters.css'

function dependenciesTabLinkWithStatus(tabLink: string) {
  return (status: string) => `${tabLink}&mode=list&state=${status}`
}

type StatusGroups = {
  finishedSuccessful: number
  runningSuccessful: number
  finishedFailed: number
  runningFailed: number
  failedToStart: number
  canceled: number
  queued: number
  overallCount: number
  overdue: number
}

type StatusesProps = {
  dependenciesTabLink: string
} & StatusGroups

function Statuses({
  finishedSuccessful,
  runningSuccessful,
  finishedFailed,
  runningFailed,
  failedToStart,
  canceled,
  queued,
  overdue,
  dependenciesTabLink,
}: StatusesProps) {
  const statusLink = dependenciesTabLinkWithStatus(dependenciesTabLink)
  const finished = finishedSuccessful + finishedFailed
  let finishedNode = null
  if (finished > 0) {
    const hrefSuccessful = statusLink('successful')
    const hrefFailed = statusLink('failed')
    finishedNode = (
      <span>
        {`${finished} finished: `}
        <RouterLink to={hrefFailed} className={styles.failed}>
          {`${finishedFailed} failed`}
        </RouterLink>
        {', '}
        <RouterLink to={hrefSuccessful} className={styles.successful}>
          {`${finishedSuccessful} successful`}
        </RouterLink>
      </span>
    )
    if (finished === finishedFailed) {
      finishedNode = (
        <RouterLink to={hrefFailed} className={styles.failed}>
          {`${finished} finished with failure`}
        </RouterLink>
      )
    }
    if (finished === finishedSuccessful) {
      finishedNode = (
        <RouterLink to={hrefSuccessful} className={styles.successful}>
          {`${finished} finished successfully`}
        </RouterLink>
      )
    }
  }

  const running = runningSuccessful + runningFailed
  let runningNode = null
  if (running > 0) {
    const link = statusLink('running')
    runningNode = (
      <span>
        <Icon name="running_static" className={classNames(styles.icon, styles.running)} />
        <RouterLink to={link} className={styles.text}>{`${running} running`}</RouterLink>
        {': '}
        <span className={styles.failed}>{`${runningFailed} failed`}</span>
        {', '}
        <span className={styles.successful}>{`${runningSuccessful} still successful`}</span>
      </span>
    )
    if (running === runningFailed) {
      runningNode = (
        <RouterLinkWrapper>
          <LinkWithIcon icon="running_static" href={link} className={styles.failed}>
            {`${running} failed and running`}
          </LinkWithIcon>
        </RouterLinkWrapper>
      )
    }
    if (running === runningSuccessful) {
      runningNode = (
        <RouterLinkWrapper>
          <LinkWithIcon icon="running_static" href={link} className={styles.successful}>
            {`${running} running and still successful`}
          </LinkWithIcon>
        </RouterLinkWrapper>
      )
    }
  }

  const failedToStartNode =
    failedToStart > 0 ? (
      <RouterLinkWrapper>
        <LinkWithIcon icon="failedToStart" href={statusLink('failedToStart')}>
          {`${failedToStart} failed to start`}
        </LinkWithIcon>
      </RouterLinkWrapper>
    ) : null

  const canceledNode =
    canceled > 0 ? (
      <RouterLinkWrapper>
        <LinkWithIcon icon="canceled" href={statusLink('canceled')}>
          {`${canceled} canceled`}
        </LinkWithIcon>
      </RouterLinkWrapper>
    ) : null

  const queuedNode =
    queued > 0 ? (
      <RouterLinkWrapper>
        <LinkWithIcon icon="queued" href={statusLink('queued')}>
          {`${queued} in queue`}
        </LinkWithIcon>
      </RouterLinkWrapper>
    ) : null

  const overtimedNode =
    overdue > 0 ? (
      <RouterLinkWrapper>
        <LinkWithIcon
          iconInnerClassName={styles.overtimeIcon}
          icon="overtime"
          href={statusLink('running')}
          tailLength={3}
        >
          {`${overdue} overdue`}
        </LinkWithIcon>
      </RouterLinkWrapper>
    ) : null

  return (
    <td className={styles.statuses}>
      {finishedNode}
      {runningNode}
      {failedToStartNode}
      {canceledNode}
      {queuedNode}
      {overtimedNode}
    </td>
  )
}

type SnapshotDependenciesCountersProps = {
  locator?: string
  isReady: boolean
  dependenciesTabLink: string
} & StatusGroups

function SnapshotDependenciesCounters({
  locator,
  isReady,
  dependenciesTabLink,
  ...stats
}: SnapshotDependenciesCountersProps) {
  return (
    <BuildsFetcher withRunning withQueued locator={locator}>
      {stats.overallCount > 0 ? (
        <tr>
          <th>
            <RouterLink to={dependenciesTabLink} className={styles.headingLink}>
              {'Dependencies'}
            </RouterLink>
          </th>
          {isReady ? (
            <Statuses dependenciesTabLink={dependenciesTabLink} {...stats} />
          ) : (
            <td>
              <LoaderInline>{'Loading...'}</LoaderInline>
            </td>
          )}
        </tr>
      ) : null}
    </BuildsFetcher>
  )
}

export default React.memo(SnapshotDependenciesCounters)
